import type { Key } from 'react';

import { DataTable, type DataTableProps } from '@/components/DataTable';
import { cn } from '@/lib/utils';

export type FitScoreLegacyRowKey<T extends object> = Key | ((record: T) => Key);

export type FitScoreTableProps<T extends object> = Omit<
  DataTableProps<T>,
  'pagination' | 'data' | 'getRowId'
> & {
  data?: T[];
  dataSource?: T[];
  getRowId?: DataTableProps<T>['getRowId'];
  rowKey?: FitScoreLegacyRowKey<T>;
};

const resolveGetRowId = <T extends object>(
  rowKey: FitScoreLegacyRowKey<T> | undefined,
  getRowId: DataTableProps<T>['getRowId'] | undefined,
): DataTableProps<T>['getRowId'] | undefined => {
  if (getRowId != null) {
    return getRowId;
  }

  if (typeof rowKey === 'function') {
    return (original) => String(rowKey(original));
  }

  if (rowKey != null) {
    return (original) => String(original[rowKey as keyof T]);
  }

  return undefined;
};

const FitScoreTable = <T extends object>({
  data,
  dataSource,
  className,
  tableClassName,
  rowKey,
  getRowId,
  showHeader = false,
  ...rest
}: FitScoreTableProps<T>) => {
  const rows = data ?? dataSource ?? [];

  return (
    <div className="overflow-x-auto">
      <div className="px-8 py-5">
        <DataTable<T>
          {...rest}
          data={rows}
          getRowId={resolveGetRowId(rowKey, getRowId)}
          pagination={false}
          showHeader={showHeader}
          className={className}
          tableClassName={cn(
            'border-separate border-spacing-0 [&_tbody_td]:border-transparent!',
            tableClassName,
          )}
        />
      </div>
    </div>
  );
};

export default FitScoreTable;
